// Account statement import (bank, Wave, Orange Money). Pure helpers, tested in financeStatement.test.js.
// A statement lists every movement of ONE account; only money going out can be a Jamora expense, and only the person
// sorting the lines knows which ones are. The selected lines are then sent as ordinary import rows
// (POST /finance/imports with kind "statement"): the server interprets them with the expense form's rules.
export const STATEMENT_FIELDS = [
  { key: "date", label: "Date", required: true },
  { key: "label", label: "Libellé / description" },
  { key: "counterparty", label: "Bénéficiaire / contrepartie", hint: "À qui l'argent a été envoyé." },
  { key: "amount", label: "Montant (signé)", hint: "Négatif = sortie d'argent." },
  { key: "debit", label: "Débit (sortie)" },
  { key: "credit", label: "Crédit (entrée)" },
  { key: "reference", label: "Référence / ID de transaction", hint: "Sert à reconnaître une ligne déjà importée." },
];

const fold = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const ALIASES = {
  date: ["date", "date operation", "date de l operation", "date transaction", "date valeur", "date de valeur", "jour", "horodatage", "timestamp"],
  label: ["libelle", "description", "operation", "details", "detail", "motif", "type", "type de transaction", "nature", "intitule"],
  counterparty: ["contrepartie", "beneficiaire", "destinataire", "nom", "nom de la contrepartie", "marchand", "commercant", "tiers", "counterparty", "to"],
  amount: ["montant", "amount", "valeur", "montant xof", "montant fcfa"],
  debit: ["debit", "debits", "sortie", "sorties", "retrait", "montant debit", "debit xof"],
  credit: ["credit", "credits", "entree", "entrees", "depot", "montant credit", "credit xof"],
  reference: ["reference", "ref", "id", "id transaction", "transaction id", "id de transaction", "numero de transaction", "n transaction", "numero"],
};

export function guessStatementMapping(headerRow) {
  const mapping = {};
  (headerRow || []).forEach((raw, index) => {
    const key = fold(raw);
    const field = Object.keys(ALIASES).find((f) => ALIASES[f].includes(key));
    if (field && mapping[field] === undefined) mapping[field] = index;
  });
  return mapping;
}

export function statementMappingProblems(mapping) {
  const problems = [];
  if (mapping.date === undefined) problems.push("la colonne « Date »");
  if (mapping.amount === undefined && mapping.debit === undefined) problems.push("une colonne « Montant » ou « Débit »");
  if (mapping.label === undefined && mapping.counterparty === undefined) problems.push("une colonne « Libellé » ou « Bénéficiaire »");
  return problems;
}

// "−12 500,50 FCFA" → { negative: true, text: "12 500,50" }; null when the cell holds no number.
// Only the SIGN is decided here; the server parses the amount itself.
export function readMoney(raw) {
  const cleaned = (raw || "").replace(/[−–]/g, "-").replace(/[^\d,.\-()\s  ]/g, "").trim();
  if (!/\d/.test(cleaned)) return null;
  const negative = /^-|-$|^\(.*\)$/.test(cleaned.replace(/\s/g, ""));
  const text = cleaned.replace(/[-()]/g, "").trim();
  return /[1-9]/.test(text) ? { negative, text } : null;
}

// Every data line of the statement, with its direction. A line is outgoing when its debit cell holds an amount,
// or when the signed amount is negative; with both debit and credit columns, an empty debit means incoming.
export function statementLines(table, mapping) {
  const cell = (cells, field) => (mapping[field] === undefined ? "" : (cells[mapping[field]] ?? "").trim());
  return table.slice(1).map((cells, i) => {
    const debit = readMoney(cell(cells, "debit"));
    const signed = readMoney(cell(cells, "amount"));
    const credit = readMoney(cell(cells, "credit"));
    let money = null;
    let outgoing = false;
    if (debit) { money = debit; outgoing = true; } else if (mapping.debit === undefined && signed) { money = signed; outgoing = signed.negative; }
    else if (credit || signed) money = credit || signed;
    const label = cell(cells, "label");
    const counterparty = cell(cells, "counterparty");
    return {
      line: i + 2, date: cell(cells, "date"), label, counterparty, reference: cell(cells, "reference"),
      amount: money ? money.text : "", outgoing: outgoing && Boolean(money),
      title: [counterparty, label].filter(Boolean).join(" · ").slice(0, 180) || "Paiement",
    };
  });
}

// Selected outgoing lines → import rows. The payer is the account holder for every line ("Jamora" or a collaborator
// reference, which the server matches exactly); the transaction id goes to the reference used for duplicates.
export function statementRows(lines, choices, { payer, currency, source }) {
  return lines.filter((l) => l.outgoing && choices[l.line]?.selected).map((l) => {
    const choice = choices[l.line];
    const row = { line: l.line, date: l.date, title: (choice.title ?? l.title).trim(), amount: l.amount, paid_by: payer };
    if (currency) row.currency = currency;
    if (choice.category) row.category = choice.category;
    if (l.counterparty) row.supplier = l.counterparty;
    if (l.reference) row.invoice_reference = l.reference;
    row.description = [`Relevé ${source}`, l.label && l.label !== row.title ? l.label : null].filter(Boolean).join(" · ");
    return row;
  });
}

export const STATEMENT_SOURCES = ["Banque", "Wave", "Orange Money", "Free Money", "Autre"];

// Mobile-money accounts in Senegal are always in CFA francs; a bank account follows the CRM's default currency
// unless the person picks another one.
const MOBILE_MONEY = new Set(["Wave", "Orange Money", "Free Money"]);
export const statementCurrency = (source, chosen, fallback) => chosen || (MOBILE_MONEY.has(source) ? "XOF" : fallback);
